import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import { Field, inputCls } from "./Field";
import { FileUpload } from "./FileUpload";

export const EMPTY_KYC = {
  aadhaar_number: "", has_pan: true, pan_number: "", payout_method: "bank", account_holder_name: "", account_number: "", ifsc: "",
  upi_id: "", aadhaar_file: null, pan_file: null, passbook_file: null, photo_file: null,
};

export const kycMissing = (k) => {
  if (!k.photo_file) return "Upload a passport size photo";
  if (k.aadhaar_number.replace(/\s/g, "").length !== 12) return "Enter your 12-digit Aadhaar number";
  if (!k.aadhaar_file) return "Upload your Aadhaar card";
  if (k.has_pan && k.pan_number.length !== 10) return "Enter your 10-character PAN or untick 'I have a PAN card'";
  if (k.has_pan && !k.pan_file) return "Upload your PAN card";
  if (k.payout_method === "bank" && !k.passbook_file) return "Upload your bank passbook photo";
  return null;
};

export const kycPayload = (k) => ({
  aadhaar_number: k.aadhaar_number.replace(/\s/g, ""), has_pan: k.has_pan, pan_number: k.has_pan ? k.pan_number : null, payout_method: k.payout_method,
  account_holder_name: k.account_holder_name || null, account_number: k.account_number || null, ifsc: k.ifsc || null,
  upi_id: k.upi_id || null, aadhaar_file_id: k.aadhaar_file?.id, photo_file_id: k.photo_file?.id, pan_file_id: k.has_pan ? k.pan_file?.id : null, passbook_file_id: k.passbook_file?.id || null,
});

const formatAadhaar = (v) => v.replace(/\D/g, "").slice(0, 12).replace(/(\d{4})(?=\d)/g, "$1 ");

export const KycSection = ({ prefix, kyc, setKyc, employer }) => {
  const set = (key) => (v) => setKyc((prev) => ({ ...prev, [key]: v }));
  const text = (key, fn = (x) => x) => (e) => set(key)(fn(e.target.value));
  const bank = kyc.payout_method === "bank";

  return (
    <div data-testid={`${prefix}-kyc-section`} className="grid grid-cols-1 gap-5 border-t border-white/10 pt-6 md:col-span-2 md:grid-cols-2">
      <div className="md:col-span-2">
        <p className="font-heading text-lg font-bold text-white">KYC & payment details</p>
        <p className="mt-1 text-sm text-slate-400">Required. Documents are kept private and checked by our team before approval.</p>
      </div>
      <FileUpload testId={`${prefix}-upload-photo`} label={employer ? "HR passport photo" : "Passport size photo"} docType="photo"
        value={kyc.photo_file} onChange={set("photo_file")} maxMb={2} accept="image/jpeg,image/png" hint="JPG or PNG, max 2 MB" />
      <div className="hidden md:block" />
      <Field label={employer ? "Authorised signatory Aadhaar" : "Aadhaar number"}>
        <Input required inputMode="numeric" data-testid={`${prefix}-input-aadhaar`} className={`${inputCls} font-mono tracking-wider`}
          value={kyc.aadhaar_number} onChange={text("aadhaar_number", formatAadhaar)} placeholder="XXXX XXXX XXXX" />
      </Field>
      <FileUpload testId={`${prefix}-upload-aadhaar`} label="Aadhaar card photo" docType="aadhaar" value={kyc.aadhaar_file} onChange={set("aadhaar_file")} />
      <label className="flex cursor-pointer items-center gap-3 text-sm text-slate-300 md:col-span-2">
        <Checkbox data-testid={`${prefix}-checkbox-has-pan`} checked={kyc.has_pan} onCheckedChange={(v) => set("has_pan")(v === true)}
          className="rounded-none border-slate-500 data-[state=checked]:border-orange-500 data-[state=checked]:bg-orange-500" />
        {employer ? "We have a PAN card" : "I have a PAN card"} <span className="text-slate-500">(untick if you don't have one)</span>
      </label>
      {kyc.has_pan && (
        <>
          <Field label={employer ? "Company / proprietor PAN" : "PAN number"}>
            <Input required maxLength={10} data-testid={`${prefix}-input-pan`} className={`${inputCls} font-mono uppercase tracking-wider`}
              value={kyc.pan_number} onChange={text("pan_number", (x) => x.toUpperCase().replace(/[^A-Z0-9]/g, ""))} placeholder="ABCDE1234F" />
          </Field>
          <FileUpload testId={`${prefix}-upload-pan`} label="PAN card photo" docType="pan" value={kyc.pan_file} onChange={set("pan_file")} />
        </>
      )}
      <div className="md:col-span-2">
        <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400">Payment method</p>
        <div className="flex gap-2">
          {[["bank", "Bank account"], ["upi", "UPI ID"]].map(([v, l]) => (
            <button key={v} type="button" onClick={() => set("payout_method")(v)} data-testid={`${prefix}-payout-${v}`}
              className={`border px-5 py-2.5 text-sm transition-colors ${kyc.payout_method === v ? "border-orange-500 bg-orange-500/10 text-orange-300" : "border-white/10 text-slate-400 hover:text-white"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>
      {bank ? (
        <>
          <Field label="Account holder name">
            <Input required data-testid={`${prefix}-input-account-holder`} className={inputCls} value={kyc.account_holder_name}
              onChange={text("account_holder_name")} placeholder="As per bank records" />
          </Field>
          <Field label="Account number">
            <Input required inputMode="numeric" maxLength={18} data-testid={`${prefix}-input-account-number`} className={`${inputCls} font-mono`}
              value={kyc.account_number} onChange={text("account_number", (x) => x.replace(/\D/g, ""))} placeholder="9-18 digits" />
          </Field>
          <Field label="IFSC code">
            <Input required maxLength={11} data-testid={`${prefix}-input-ifsc`} className={`${inputCls} font-mono uppercase`}
              value={kyc.ifsc} onChange={text("ifsc", (x) => x.toUpperCase().replace(/[^A-Z0-9]/g, ""))} placeholder="SBIN0001234" />
          </Field>
          <FileUpload testId={`${prefix}-upload-passbook`} label="Bank passbook / cancelled cheque" docType="passbook" value={kyc.passbook_file} onChange={set("passbook_file")} />
        </>
      ) : (
        <Field label="UPI ID">
          <Input required data-testid={`${prefix}-input-upi`} className={inputCls} value={kyc.upi_id}
            onChange={text("upi_id", (x) => x.trim())} placeholder="name@bank" />
        </Field>
      )}
    </div>
  );
};
